import React, { useCallback, useMemo, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import {
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { ThinPlusInCircle } from "assets/material-custom-icons";
import { AsyncContentMui } from "components/Async-content/AsyncContentMui";
import { FacilityCreationWizard, FacilityCreationWizardProps } from "components/Deal-amortization/FacilityCreationWizard";
import { RoleComponentGuard } from "components/guards/Role-component.guard";
import { NavigateLink } from "components/Navigate-link/NavigateLink";
import { useActionHook } from "hooks";
import { useDealDefaultCurrencySelect, useDealIdSelector, useDealSelector, useDebtActions } from "store";
import { CurrencyType, DebtSegment } from "store/types";
import { formatNumber } from "utils";
import { getFacilitySegment, getLastSegmentsByDealId, getSegmentsOfDeal } from "utils/debt";

import { currencySign, RoleName } from "../../constants";

interface Props {
  toDate: string;
}

export const SubDealsCard: React.FC<Props> = ({ toDate }) => {
  const deal = useDealSelector();
  const [open, setOpen] = useState(false);
  const currency = useDealDefaultCurrencySelect();
  const dealId = useDealIdSelector() ?? 0; // This page exist only in deal page context.

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["amortizationTable", deal.id, toDate],
    queryFn: () => (deal.id ? getAmortizationTableAction(deal.id, toDate) : null)
  });

  const { getAmortizationTableAction, createWithdrawalAction, updateBulkDebtSegmentsAction } = useDebtActions();

  const { action: createWithdrawal, loading: createWithdrawalLoading } = useActionHook(createWithdrawalAction);
  const { action: updateBulkAction, loading: updateBulkLoading } = useActionHook(updateBulkDebtSegmentsAction);

  const handleApply: FacilityCreationWizardProps["onApply"] = useCallback(
    async (segments, targetDealId, fees) => {
      const dealIdToUse = targetDealId || dealId;

      // When targetDealId is a withdrawal deal, include all its segments so the
      // server doesn't delete the ones not in the request.
      let segmentsToSend = segments;
      if (targetDealId && data?.segments) {
        const allForDeal = data.segments.filter((s) => s.deal_id === targetDealId);
        const incomingById = new Map(segments.map((s) => [s.id, s]));
        segmentsToSend = allForDeal.map((s) => incomingById.get(s.id) ?? s);
      }

      await updateBulkAction({ dealId: dealIdToUse, segments: segmentsToSend, fees });
      refetch();
    },
    [dealId, data, updateBulkAction, refetch]
  );

  const handleCreateWithdrawal = useCallback(
    async (segment: DebtSegment) => {
      const response = await createWithdrawal(dealId, segment);
      refetch();
      return response;
    },
    [dealId, createWithdrawal, refetch]
  );

  const handleClose = () => {
    setOpen(false);
  };

  const reportPayment = useMemo(() => {
    return data?.table.rows.find((row) => row.date === toDate);
  }, [data, toDate]);

  const subPayments = reportPayment ? reportPayment.children || [reportPayment] : [];

  const lastSegments = data ? getLastSegmentsByDealId(data.segments) : [];
  const facilitySegment = getFacilitySegment(lastSegments);
  const ownSegments = getSegmentsOfDeal(data?.segments || [], dealId);
  const isFacility = ownSegments.every((segment) => segment.is_facility && !segment.is_withdrawal);
  const isNew = !ownSegments.length;

  // Get ALL segments for facility and withdrawals (not just last)
  const facilityDealId = facilitySegment?.deal_id;
  const facilitySegments =
    facilityDealId && data?.segments ? data.segments.filter((s) => s.deal_id === facilityDealId) : [];
  const withdrawalSegments = data?.segments ? data.segments.filter((s) => s.is_withdrawal && !s.is_facility) : [];

  const formatAmount = (value: number) => {
    const formattedNumber = formatNumber(value);
    return `${currencySign[currency as CurrencyType]}${formattedNumber}`;
  };

  return (
    <Stack
      sx={{
        gap: "8px",
        borderRadius: 16,
        p: "16px",
        bgcolor: "white",
        boxShadow: "sm",
        overflow: "hidden",
        flexGrow: 5
      }}
    >
      <AsyncContentMui spinnerProps={{ size: 100 }} isLoading={isLoading}>
        <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center" }}>
          <Typography sx={{ fontSize: "18px", color: "#071F35", fontWeight: 500 }}>
            {isFacility ? "Drawdowns" : "Loan status"}
          </Typography>
          {isFacility && (
            <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]}>
              <IconButton
                onClick={() => setOpen(true)}
                size="sm"
                sx={{
                  bgcolor: "#071F35",
                  ".MuiSvgIcon-root": { color: "white" },
                  ":hover": { bgcolor: "#071F35", ".MuiSvgIcon-root": { color: "white" } }
                }}
              >
                <AddIcon />
              </IconButton>
            </RoleComponentGuard>
          )}
        </Stack>
        {subPayments.length > 0 ? (
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              maxHeight: 400,
              overflow: "auto",
              borderColor: "#DBE8F5",
              borderRadius: "8px",
              border: "1px solid #DBE8F5"
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ bgcolor: "#F5F9FF" }}>
                  <TableCell sx={{ fontWeight: 600, color: "#071F35" }}>Deal Name</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#071F35" }}>Investment Sum</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#071F35" }}>Outstanding Principal</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#071F35" }}>Accrued Interest</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#071F35" }}>Accrued PIK</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#071F35" }}>Loan Balance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {subPayments.map((payment) => {
                  const segments = payment.segmentIds.map((id) => data?.segments.find((segment) => segment.id === id)!);
                  return (
                    <TableRow key={segments[0].id} sx={{ borderBottom: "1px solid #DBE8F5" }}>
                      <TableCell>
                        {payment.deal_id ? (
                          <NavigateLink
                            allowNewTab={true}
                            route="dealDashboard"
                            params={{ deal_id: String(payment.deal_id) }}
                            style={{ color: "#26a6ae" }}
                          >
                            {payment.sub_deal}
                          </NavigateLink>
                        ) : (
                          <Typography>{payment.sub_deal}</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {formatAmount(segments.reduce((acc, segment) => acc + segment.loan_amount, 0))}
                      </TableCell>
                      <TableCell>{formatAmount(payment?.actual_principal || 0)}</TableCell>
                      <TableCell>{formatAmount(payment?.actual_accrued_interest || 0)}</TableCell>
                      <TableCell>{formatAmount(payment?.actual_accrued_pik || 0)}</TableCell>
                      <TableCell>
                        {formatAmount(
                          payment?.loan_balance
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Stack
            onClick={() => setOpen(true)}
            sx={{
              flexGrow: 1,
              p: "10px",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "#F6F8FC",
              cursor: "pointer"
            }}
          >
            <ThinPlusInCircle sx={{ fontSize: "24px" }} />
            <Typography sx={{ fontSize: "15px", color: "#071F35", fontWeight: 400 }}>
              {facilitySegment ? "Add new Drawdown" : "Create facility"}
            </Typography>
          </Stack>
        )}
      </AsyncContentMui>
      <FacilityCreationWizard
        open={open}
        editMode={!isNew}
        onClose={handleClose}
        onApply={handleApply}
        onCreateWithdrawal={handleCreateWithdrawal}
        loading={updateBulkLoading || createWithdrawalLoading}
        existingSegments={facilitySegments}
        existingFees={data?.fees}
        existingWithdrawals={withdrawalSegments}
      />
    </Stack>
  );
};
